import { FC, ReactNode, useState } from 'react';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	notification,
	Row,
	Select,
	// Select,
	Space,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import {
	FileData,
	ProspectEmploymentContract,
} from '@/libs/fleet-api/services/types';
import { useForm } from '@/libs/antd/form';
import { useFRMPraSurveyInput } from './PraSurveyInputPage';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import {
	useSelectBouwheer,
	useSelectPaymentMethod,
} from '@/libs/fleet-api/hooks/master-data';
import dayjs from 'dayjs';
import rule from '@/libs/antd/rules';
import {
	addEmploymentContract,
	deleteEmploymentContract,
	submitPraSurvey,
	updateEmploymentContract,
} from '@/libs/fleet-api/services/survey';
import { useApp } from '@/App';
import { useMutation } from '@tanstack/react-query';
import { thousandFormat } from '@/libs/antd/formater';
import { inputThousand } from '@/libs/antd/input-number';
import { uploader } from '@/libs/fleet-api/uploader';
import { useModals } from '@/components/modals/ModalProvider';

type FRMPraSurveyInputEmploymentContractFormProps = {
	children?: ReactNode;
	onPrev?: () => void;
	validationStatus?: {
		legal: boolean;
		business: boolean;
		referral: boolean;
		financial: boolean;
		bank: boolean;
		contract: boolean;
	};
};

type EmploymentContractForm = {
	input?: ProspectEmploymentContract & { bouwheerId: string };
	items: ProspectEmploymentContract[];
};

export const useFRMPraSurveyInputEmploymentContractForm = () => {
	return useForm<EmploymentContractForm>();
};

const FRMPraSurveyInputEmploymentContractForm: FC<
	FRMPraSurveyInputEmploymentContractFormProps
> = ({
	children,
	// onPrev = () => false,
	validationStatus = {
		legal: false,
		business: false,
		referral: false,
		financial: false,
		bank: false,
		contract: false,
	},
}) => {
	const { mainInformation, employmentContractForm, employmentContracts } =
		useFRMPraSurveyInput();
	const prospectId = mainInformation?.prospectId;
	const { form, value, fieldName } = employmentContractForm;
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);
	const { navigate } = useApp();
	const { openEmploymentContractModal } = useModals();

	const addEmploymentContractMutation = useMutation({
		mutationFn: addEmploymentContract,
	});

	const updateEmploymentContractMutation = useMutation({
		mutationFn: updateEmploymentContract,
	});

	const deleteEmploymentContractMutation = useMutation({
		mutationFn: deleteEmploymentContract,
	});

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const selectBouwheer = useSelectBouwheer();

	const selectPaymentMethod = useSelectPaymentMethod();

	const isJF = mainInformation?.customerType?.id === 1;
	const notRequireSurvey =
		mainInformation?.isSurveyByBankMandiri &&
		mainInformation?.bankMandiriSurveyAt &&
		dayjs(mainInformation?.bankMandiriSurveyAt).isAfter(
			dayjs().subtract(6, 'month'),
		);

	const handleSubmit = async ({ input, items }: EmploymentContractForm) => {
		const payload: ProspectEmploymentContract<'payload'> = {
			prospectId: mainInformation!.prospectId,
			bouwheerId: input!.bouwheerId,
			contractDetail: input!.contractDetail,
			contractValue: input!.contractValue,
			endDate: input!.endDate,
			startDate: input!.startDate,
			jobLocation: input!.jobLocation,
			jobTitle: input!.jobTitle,
			spkNumber: input!.spkNumber,
			termOfPayment: input?.termOfPayment,
			paymentMethod: input?.paymentMethod?.id,
			tenor: input?.tenor,
			contractDocument: {
				id: input!.contractDocument.id,
				name: input!.contractDocument.name,
				path: input!.contractDocument.path,
			},
		};

		if (modal?.type === 'edit') {
			await updateEmploymentContractMutation.mutateAsync({
				...payload,
				id: input!.id,
			});

			form.setFieldsValue({
				items: items.map((list, index) => {
					return index === modal.index ? input! : list;
				}),
			});
			setModal(null);
		} else {
			const res = await addEmploymentContractMutation.mutateAsync(payload);
			form.setFieldsValue({
				items: [...items, { ...input!, id: res.data.id }],
			});
			setModal(null);
		}
	};

	const getInitialValues = () => {
		return {
			items:
				employmentContracts?.map((data: ProspectEmploymentContract) => {
					return {
						id: data.id,
						bouwheer: {
							id: data.bouwheer.id,
							name: data.bouwheer.name,
							industrySector: data.bouwheer.industrySector,
							type: data.bouwheer.type,
						},
						bouwheerId: data.bouwheer.id,
						jobTitle: data.jobTitle,
						spkNumber: data.spkNumber,
						jobLocation: data.jobLocation,
						contractDetail: data.contractDetail,
						contractValue: data.contractValue,
						startDate: data.startDate,
						endDate: data.endDate,
						termOfPayment: data.termOfPayment,
						tenor: data.tenor,
						paymentMethod: data.paymentMethod?.id,
						contractDocument: data.contractDocument,
					} as ProspectEmploymentContract & { bouwheerId: string };
				}) ?? [],
			input: undefined,
		} as EmploymentContractForm;
	};

	const handleFinalSubmit = async () => {
		if (prospectId) {
			if (!Object.values(validationStatus).every((status: boolean) => status)) {
				notification.error({
					message: 'Belum bisa melanjutkan',
					description: (
						<>
							Terdapat data yang belum lengkap:
							<ul>
								{!validationStatus.legal && <li>Dokumen Legalitas</li>}
								{!validationStatus.business && <li>Dokumen Perizinan Usaha</li>}
								{!validationStatus.referral && <li>Dokumen Referral</li>}
								{!validationStatus.financial && (
									<li>Financial Information minimal 1</li>
								)}
								{!validationStatus.bank && <li>Rekening Bank minimal 1</li>}
								{!validationStatus.contract && <li>Kontrak kerja minimal 1</li>}
							</ul>
						</>
					),
				});
				return;
			}

			const { data } = await submitPraSurvey({
				prospectId,
			});
			if (data) {
				notification.success({
					message: 'Success',
					description: 'Data berhasil disimpan.',
				});
				if (isJF && notRequireSurvey) {
					navigate('FRMPipelineListPage');
				} else {
					navigate('FRMRequestSurveyPage');
				}
			}
		}
	};

	const isLoading = addEmploymentContractMutation.isPending;
	const { modal: modalConfirm } = useModals();

	return (
		<Form.Provider>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				initialValues={getInitialValues()}
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['items'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as EmploymentContractForm;
						return (
							<Space {...ANTD_DEFAULT_PROPS.SPACE}>
								<Table
									bordered
									loading={isLoading}
									scroll={{ x: 2000 }}
									dataSource={
										values?.items?.map((item, key) => {
											return { ...item, key };
										}) ?? []
									}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											width: 1,
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	openEmploymentContractModal(record);
																}}
															>
																<FileSearchOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName('input'), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalConfirm.confirm({
																			title:
																				'Anda yakin akan menghapus data ini?',
																		});
																	if (confirmDelete) {
																		form.setFieldsValue({
																			items: values.items.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																		deleteEmploymentContractMutation.mutateAsync(
																			record.id!,
																		);
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
										},
										{
											title: 'Bouwheer',
											dataIndex: ['bouwheer', 'name'],
										},
										{
											title: 'Bouwheer ID',
											dataIndex: ['bouwheer', 'id'],
										},
										{
											title: 'Kelas Industri',
											dataIndex: [
												'bouwheer',
												'industrySector',
												'industryClass',
											],
										},
										{
											title: 'Nama Pekerjaan',
											dataIndex: ['jobTitle'],
										},
										{
											title: 'No. SPK',
											dataIndex: ['spkNumber'],
										},
										{
											title: 'Lokasi Pekerjaan',
											dataIndex: ['jobLocation'],
										},
										{
											title: 'Detail Kontrak',
											render: (_, record) => (
												<span className="whitespace-pre-wrap">
													{record.contractDetail}
												</span>
											),
										},
										{
											title: 'Nilai Kontrak',
											render: (_, record) => (
												<span>{thousandFormat(record.contractValue)}</span>
											),
										},
										{
											title: 'Tanggal Mulai',
											render: (_, record) =>
												dayjs(record.startDate).format('DD/MM/YYYY'),
										},
										{
											title: 'Tanggal Berakhir',
											render: (_, record) =>
												dayjs(record.endDate).format('DD/MM/YYYY'),
										},
										{
											title: 'Tenor',
											render: (_, record) => record.tenor ?? '-',
										},
										{
											title: 'Sistem Pembayaran',
											render: (_, record) => record.paymentMethod?.value ?? '-',
										},
										{
											title: 'TOP Pembayaran',
											render: (_, record) => record.termOfPayment ?? '-',
										},
									]}
									pagination={false}
								/>

								{children}

								<Space
									style={{ display: 'flex', justifyContent: 'end' }}
									size="large"
								>
									{/* <Button
										style={{
											width: 120,
											display: 'block',
										}}
										size="small"
										color="secondary"
										loading={isLoading}
										onClick={onPrev}
									>
										Back
									</Button> */}
									<Space
										style={{ display: 'flex', justifyContent: 'end' }}
										size="large"
									>
										<Button
											style={{
												width: 120,
												display: 'block',
											}}
											onClick={() => setModal({ type: 'add' })}
											loading={isLoading}
										>
											ADD
										</Button>
										<Button
											type="primary"
											style={{
												width: 200,
												display: 'block',
											}}
											loading={isLoading}
											disabled={(value?.items?.length ?? 0) < 1}
											onClick={handleFinalSubmit}
										>
											SAVE
										</Button>
									</Space>
								</Space>

								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form?.submit();
									}}
								>
									<FleetTitle>Add Kontrak Kerja</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'bouwheer'])}
												noStyle
											/>
											<Form.Item
												label="Bouwheer"
												name={fieldName(['input', 'bouwheerId'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Select
													{...selectBouwheer}
													options={
														!values.input?.bouwheerId ||
														selectBouwheer.options?.length
															? selectBouwheer.options
															: [
																	{
																		label: values.input.bouwheer.name,
																		value: values.input.bouwheer.id,
																	},
																]
													}
													onSelect={(value, option) => {
														selectBouwheer.onSelect!(value, option);
														setFieldValue(
															fieldName(['input', 'bouwheer']),
															option.data,
														);
													}}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Bouwheer Name"
												name={fieldName(['input', 'bouwheer', 'name'])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Bouwheer Type"
												name={fieldName(['input', 'bouwheer', 'type'])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Kelas Industri"
												name={fieldName([
													'input',
													'bouwheer',
													'industrySector',
													'industryClass',
												])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Sektor Industri LV 5"
												name={fieldName([
													'input',
													'bouwheer',
													'industrySector',
													'sectorLevel5',
												])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Sektor Industri LV 4"
												name={fieldName([
													'input',
													'bouwheer',
													'industrySector',
													'sectorLevel4',
												])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Sektor Industri LV 2"
												name={fieldName([
													'input',
													'bouwheer',
													'industrySector',
													'sectorLevel2',
												])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Sektor Industri LV 1"
												name={fieldName([
													'input',
													'bouwheer',
													'industrySector',
													'sectorLevel1',
												])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Sektor Industri LV 3"
												name={fieldName([
													'input',
													'bouwheer',
													'industrySector',
													'sectorLevel3',
												])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Nama Pekerjaan"
												name={fieldName(['input', 'jobTitle'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="No. SPK"
												name={fieldName(['input', 'spkNumber'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Lokasi Pekerjaan"
												name={fieldName(['input', 'jobLocation'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={24}>
											<Form.Item
												label="Detail Kontrak"
												name={fieldName(['input', 'contractDetail'])}
												labelCol={{ span: 5 }}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[rule.required()]}
											>
												<Input.TextArea rows={4} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Nilai Kontrak"
												name={fieldName(['input', 'contractValue'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputThousand} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'contractDocument'])}
												label="Upload Dokumen Kontrak Kerja"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => {
													const file = value as FileData;
													return {
														fileList: file?.id
															? ([
																	{
																		uid: file.id,
																		name: file.name,
																		thumbUrl: file.url,
																	},
																] as UploadFile[])
															: [],
													};
												}}
												normalize={({ file }: { file: UploadFile }) => {
													return {
														id: file.uid,
														format: file.type,
														name: file.name,
														url: file.thumbUrl,
													} as FileData;
												}}
											>
												<Upload.Dragger
													listType="picture"
													maxCount={1}
													beforeUpload={() => false}
													onRemove={() => {
														form.setFieldValue(
															fieldName(['input', 'contractDocument']),
															null,
														);
														return false;
													}}
													onChange={async ({ file }) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/employment-contract`;

														try {
															const url = await uploadMutation.mutateAsync({
																file: file as unknown as File,
																fileid,
																filepath,
															});

															form.setFieldValue(
																fieldName(['input', 'contractDocument']),
																{
																	format: 'jpeg',
																	id: fileid,
																	name: file.name,
																	path: filepath,
																	url,
																},
															);
														} catch {
															form.setFieldValue(
																fieldName(['input', 'contractDocument']),
																null,
															);
														}
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Tanggal Mulai"
												name={fieldName(['input', 'startDate'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).format('YYYY-MM-DD')
												}
											>
												<DatePicker />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Tanggal Berakhir"
												name={fieldName(['input', 'endDate'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).format('YYYY-MM-DD')
												}
											>
												<DatePicker />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Tenor"
												name={fieldName(['input', 'tenor'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Sistem Pembayaran"
												name={fieldName(['input', 'paymentMethod'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueFromEvent={(_, option) => option?.data}
												getValueProps={(value) => {
													if (!value) return {};
													return {
														value: {
															value: value.id,
															label: value.value,
														},
													};
												}}
											>
												<Select {...selectPaymentMethod} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="TOP Pembayaran"
												name={fieldName(['input', 'termOfPayment'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
								</FleetModal>
							</Space>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMPraSurveyInputEmploymentContractForm;
